import { useEffect, useState } from "react";
import { Box } from "theme-ui";
import { useDispatch, useSelector } from "react-redux";
import filter from "lodash/fp/filter";
import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import has from "lodash/fp/has";
import isEmpty from "lodash/fp/isEmpty";
import isString from "lodash/fp/isString";
import join from "lodash/fp/join";
import map from "lodash/fp/map";
import update from "lodash/fp/update";

import { useTranslation } from "hooks";
import { createImage } from "api";
import {
  selectAllCustomisationNotifications,
  selectShowAddToOrderButton,
  selectHasAllRequiredValues,
  selectUserChoicesForUi,
  selectProductImagesForAngle,
  selectProductImageAngles,
  selectAddToOrderValues,
  selectIsOutOfStock,
} from "store/app";
import { displayShareModal, addToOrder } from "store/app/appSlice";
import { Button, Notifications } from "components";
import Modal from "../Modal";
// import { PrintIcon, DownloadIcon } from "components/icons";
import { ShareIcon } from "components/icons";
import ReviewImages from "./ReviewImages";
import * as styles from "./ModalReview.styles";

function getLabelValues(shouldJoinWith) {
  return flow(
    map(({ label, value, title, values, joinValuesWith, isIncluded }) => {
      if (values) {
        return [title, getLabelValues(joinValuesWith)(values)]
          .filter(Boolean)
          .join(": ");
      }
      return [
        label,
        [`<strong>${value}</strong>`, isIncluded && `<em>${isIncluded}</em>`]
          .filter(Boolean)
          .join(" "),
      ]
        .filter(Boolean)
        .join(": ");
    }),

    (displayValues) =>
      shouldJoinWith ? join(shouldJoinWith)(displayValues) : displayValues
  );
}

export default function ModalReview({
  appLogo,
  title,
  cost,
  expectedDelivery,
  onClose,
  closeButtonLabel,
  isTopMostModal,
  gameFitDealerLink,
}) {
  const dispatch = useDispatch();

  const { t } = useTranslation();

  const [selectedImageViewAngle, setSelectedImageViewAngle] = useState(0);
  const [promise, setPromise] = useState(null);

  const imageAngles = useSelector(selectProductImageAngles);

  const allCustomisationNotifications = useSelector(
    selectAllCustomisationNotifications
  );

  const productImagesForAngle = useSelector(
    selectProductImagesForAngle(imageAngles[selectedImageViewAngle])
  );

  const filteredProductImages = productImagesForAngle.map(
    ({ imageSet, images }) => ({
      imageSet,
      images: images.filter(has("url")),
    })
  );

  const images = filteredProductImages.map(
    update("images", flow(map(get("url")), filter(isString)))
  );

  const addToOrderValues = useSelector(selectAddToOrderValues);

  const dispatchAddToOrder = () => {
    const _promise = dispatch(
      addToOrder({
        images,
        ...addToOrderValues,
      })
    );
    setPromise(_promise);
  };

  const dispatchDisplayShareModal = () => dispatch(displayShareModal());

  const showAddToOrderButton = useSelector(selectShowAddToOrderButton);
  const userChoicesForUi = useSelector(selectUserChoicesForUi(t));
  const hasAllRequiredValues = useSelector(selectHasAllRequiredValues);
  const isOutOfStock = useSelector(selectIsOutOfStock);

  // cancel any fetch promises in progress, on component unmount
  useEffect(() => {
    return () => {
      if (promise) {
        promise.abort("cancelled");
      }
    };
  }, [promise?.requestId]);

  useEffect(() => {
    const controller = new AbortController();

    images.map((image) =>
      createImage({ signal: controller.signal })({
        images: image.images,
      })
    );

    return () => {
      controller.abort();
    };
  }, [JSON.stringify(images)]);

  return (
    <Modal
      appLogo={appLogo}
      outerTitle="Review Your Custom Build"
      title={title}
      subTitle={
        <>
          <strong>{cost}</strong> | {expectedDelivery}
        </>
      }
      onClose={onClose}
      closeButtonLabel={closeButtonLabel}
      isTopMostModal={isTopMostModal}
      data-cy="modal-review-specs"
    >
      {({ close }) => (
        <Box sx={styles.modalContent}>
          <Box sx={styles.image}>
            <ReviewImages
              selectedImageViewAngle={selectedImageViewAngle}
              setSelectedImageViewAngle={setSelectedImageViewAngle}
              imageAngles={imageAngles}
              images={filteredProductImages}
            />
          </Box>

          <Box sx={styles.actions}>
            {showAddToOrderButton && (
              <Button
                size="large"
                isRound
                onClick={dispatchAddToOrder}
                disabled={!hasAllRequiredValues || isOutOfStock}
              >
                {isOutOfStock ? t("Out of Stock") : t("Add To Order")}
              </Button>
            )}

            {!showAddToOrderButton && gameFitDealerLink && (
              <Button
                href={gameFitDealerLink.href}
                theme="dark"
                isRound
                sx={styles.fixExpertDealerLocationButton}
              >
                {gameFitDealerLink.label}
              </Button>
            )}

            <Button variant="secondary" size="small" isRound onClick={close}>
              {t("Make Additional Changes")}
            </Button>

            <Box sx={styles.shareButtonsContainer}>
              {/* <Button
                iconBefore={<PrintIcon />}
                aria-label={t("print")}
                theme="light"
                isRound
                sx={styles.iconButton}
              /> */}

              <Button
                iconBefore={<ShareIcon />}
                aria-label={t("share")}
                theme="light"
                isRound
                sx={styles.iconButton}
                onClick={dispatchDisplayShareModal}
                disabled={!hasAllRequiredValues}
              />

              {/* <Button
                iconBefore={<DownloadIcon />}
                aria-label={t("download")}
                theme="light"
                isRound
                onClick={dispatchDisplaySaveModal}
                sx={styles.iconButton}
              /> */}
            </Box>
          </Box>

          <Box sx={styles.details} data-cy="specs">
            {userChoicesForUi.map(
              ({ title, values, joinValuesWith, displayAs }) => {
                const image = values.map(get("image")).filter(Boolean)[0];
                const labelValues = getLabelValues(
                  displayAs === "row" && joinValuesWith
                )(values);

                return (
                  <Box key={title} sx={styles.item}>
                    <Box>
                      {title && `${title}: `}

                      {displayAs === "column" && !isEmpty(labelValues) ? (
                        <Box pl="20px">
                          {labelValues.map((value) => (
                            <Box
                              key={value}
                              dangerouslySetInnerHTML={{ __html: value }}
                              mt="10px"
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box
                          as="span"
                          dangerouslySetInnerHTML={{
                            __html: !isEmpty(labelValues) ? labelValues : "--",
                          }}
                        />
                      )}
                    </Box>

                    {image && (
                      <Box key={image} sx={styles.valueImage({ image })} />
                    )}
                  </Box>
                );
              }
            )}

            {allCustomisationNotifications.length > 0 && (
              <Notifications notifications={allCustomisationNotifications} />
            )}
          </Box>
        </Box>
      )}
    </Modal>
  );
}
